import { HOST_TULUM, API, HOST } from '@/utils/http'
import { toast } from "@/components/ui/toast/use-toast"

const layerToFeature = (layer) => {
  const feature = {
    id: layer.id,
    uuid: layer.code,
    type: 'Feature',
    geometry: layer.layer?.geometry ? layer.layer.geometry : null,
    properties: {
      type: layer.type,
      data_type: layer.data_type ?? null,
      parent_id: layer.parent_id ?? null,
      sort_order: layer.sort_order ?? null,
      visible: false,
      code: layer.code,
      name: layer.name,
      organization_id: layer.org_id,
      layer: layer.layer,
      created_at: layer.created_at,
      updated_at: layer.updated_at
    }
  }
  if (layer.children?.length) {
    feature.properties.children = layer.children.map(layerToFeature)
  }
  return feature
}

const patchLayerOrder = (orgId, layers) =>
  API({
    method: 'PATCH',
    url: `${HOST}/org/${orgId}/layers/order`,
    data: { layers: layers.map((l, idx) => ({ code: l.uuid, sort_order: idx })) }
  })

// Layer type 2 = subscription layer, status 0 = done
const SUBSCRIPTION_LAYER_TYPE = 2
const STATUS_DONE = 0

export const fetchSubscriptionResults = async ({ commit }, { orgId, layerCodes }) => {
  if (!layerCodes.length) return {}

  try {
    const response = await API({
      method: 'POST',
      url: `${HOST}/org/${orgId}/layers/subscription/results/batch`,
      data: { layer_codes: layerCodes }
    })

    commit('SET_SUBSCRIPTION_RESULTS', { orgId, results: response.data })
    return response.data
  } catch (error) {
    console.error('Error fetching subscription results:', error)
    return {}
  }
}

export const fetchOrgLayers = async ({ commit, dispatch }, { orgId }) => {
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId}/layers/all`
    })

    const layers = response.data.map(layerToFeature)

    commit('SET_ORG_LAYERS', { orgId, layers })

    // Batch fetch subscription results for completed or tile-generating subscription layers.
    // Status 10 (generate_tiles) means the results JSON exists and tiles are being
    // generated progressively — earlier results may already have viewable tiles.
    const STATUS_GENERATING_TILES = 10
    const subscriptionLayers = layers.filter(
      l => l.properties.type === SUBSCRIPTION_LAYER_TYPE &&
        (l.properties.layer?.status === STATUS_DONE || l.properties.layer?.status === STATUS_GENERATING_TILES)
    )
    const layerCodes = subscriptionLayers.map(l => l.properties.code)

    if (layerCodes.length > 0) {
      dispatch('fetchSubscriptionResults', { orgId, layerCodes })
    }

    return layers
  } catch (error) {
    console.error('Error fetching organization layers:', error)
  }
}

export const addOrgLayer = ({ commit }, { orgId, layer }) => {
  const featureLayer = layerToFeature(layer)
  commit('ADD_ORG_LAYER', { orgId, layer: featureLayer })
  return featureLayer
}

export const updateOrgLayer = ({ state, commit }, { orgId, layer }) => {
  // The store keys layers by ``uuid`` (mapped from the API's ``code``) and
  // nests the API payload under ``properties.layer``. Callers sometimes pass
  // a raw response and sometimes a pre-converted feature — normalize here so
  // polling from variants like LayerLulcPreview actually updates the row.
  const featureLayer = layer && layer.uuid ? layer : layerToFeature(layer)
  commit('UPDATE_ORG_LAYER', { orgId, layer: featureLayer })
  return featureLayer
}

export const updateOrgLayerFeatures = ({ commit }, { orgId, layerId, features }) => {
  commit('UPDATE_ORG_LAYER_FEATURES', { orgId, layerId, features })
  return features
}

export const moveOrgLayerUp = async ({ commit, state }, { orgId, layerId }) => {
  commit('MOVE_ORG_LAYER_UP', { orgId, layerId })
  try {
    await patchLayerOrder(orgId, state.orgLayers[orgId] || [])
  } catch (error) {
    console.error('Failed to persist layer order:', error)
  }
}

export const moveOrgLayerDown = async ({ commit, state }, { orgId, layerId }) => {
  commit('MOVE_ORG_LAYER_DOWN', { orgId, layerId })
  try {
    await patchLayerOrder(orgId, state.orgLayers[orgId] || [])
  } catch (error) {
    console.error('Failed to persist layer order:', error)
  }
}

export const reorderOrgLayers = async ({ commit }, { orgId, layers }) => {
  commit('REORDER_ORG_LAYERS', { orgId, layers })
  try {
    await patchLayerOrder(orgId, layers)
  } catch (error) {
    console.error('Failed to persist layer order:', error)
  }
}

export const reorderChildren = async ({ commit }, { orgId, parentUuid, children }) => {
  commit('REORDER_CHILDREN', { orgId, parentUuid, children })
  try {
    await patchLayerOrder(orgId, children)
  } catch (error) {
    console.error('Failed to persist children order:', error)
  }
}

export const renameOrgLayer = async ({ commit }, { orgId, layerId, name }) => {
  try {
    const trimmedName = typeof name === 'string' ? name.trim() : ''
    if (!orgId || !layerId || !trimmedName) {
      throw new Error('Missing required parameters: orgId, layerId, and name are required')
    }

    await API({
      method: 'PATCH',
      url: `${HOST}/org/${orgId}/layers/${layerId}/name`,
      data: { name: trimmedName }
    })

    commit('RENAME_ORG_LAYER', { orgId, layerId, name: trimmedName })
    return true
  } catch (error) {
    console.error('Error renaming organization layer:', error)
    throw error
  }
}

export const deleteOrgLayer = async ({ commit }, { orgId, layerId }) => {
  try {
    // Validate parameters
    if (!orgId || !layerId) {
      throw new Error('Missing required parameters: orgId and layerId are required')
    }

    // Log deletion attempt for debugging
    console.warn('DELETING LAYER:', {
      orgId,
      layerId,
      timestamp: new Date().toISOString(),
      stack: new Error().stack
    })

    await API({
      method: 'DELETE',
      url: `${HOST}/org/${orgId}/layers/${layerId}`
    })

    commit('DELETE_ORG_LAYER', { orgId, layerId })
    return true
  } catch (error) {
    console.error('Error deleting organization layer:', error)
    throw error
  }
}

export const setSelectedFeature = ({ commit }, { feature, coordinates, isVisible }) => {
  commit('SET_SELECTED_FEATURE', { feature, coordinates, isVisible })
}

export const fetchLayers = async ({commit}, payload) => {
  const response = await API({
    method: 'GET',
    url: `${HOST_TULUM}/layers/${payload.org_id}`
  })
}

export const updateLayerProperties = ({ commit, rootState }, { orgId, layerId, properties }) => {
  const resolvedOrgId = orgId || rootState.org?.selectedOrg?.id
  if (!resolvedOrgId || !layerId) {
    console.warn('updateLayerProperties: missing orgId or layerId', { orgId, layerId })
    return
  }

  commit('UPDATE_LAYER_PROPERTIES', { orgId: resolvedOrgId, layerId, properties })
}

export const setActiveResult = ({ commit }, { layerId, result }) => {
  commit('SET_ACTIVE_RESULT', { layerId, result })
}

export const setPlanetscopeSelection = ({ commit }, { layerId, selection }) => {
  commit('SET_PLANETSCOPE_SELECTION', { layerId, selection })
}

export const setMapViewSettings = ({ commit }, { mapId, settings }) => {
  commit('SET_MAP_VIEW_SETTINGS', { mapId, settings })
}

export const setOrderProductBundle = ({ commit }, { layerId, bundle }) => {
  commit('SET_ORDER_PRODUCT_BUNDLE', { layerId, bundle })
}

export const setPlanetVarsSelection = ({ commit }, { layerId, selection }) => {
  commit('SET_PLANET_VARS_SELECTION', { layerId, selection })
}

export const setLulcActiveMonth = ({ commit }, { parentCode, monthCode }) => {
  commit('SET_LULC_ACTIVE_MONTH', { parentCode, monthCode })
}

export const createProjectDataLayer = async ({ commit, getters }, { orgId, projectId, projectName }) => {
  const trimmedName = typeof projectName === 'string' ? projectName.trim() : ''
  if (!orgId || !projectId || !trimmedName) {
    throw new Error('Missing required parameters: orgId, projectId, and projectName are required')
  }

  // Check for existing layer with same project filter
  const existingLayers = getters.getOrgLayers(orgId)
  const duplicate = existingLayers.find(layer => {
    if (layer.properties?.type !== 4) return false
    const filters = layer.properties?.layer?.data?.filter?.config
    if (!Array.isArray(filters) || filters.length !== 1) return false
    const f = filters[0]
    return f.type === 'ArrayFilter' && f.field_name === 'project_id' &&
      Array.isArray(f.config) && f.config.length === 1 && String(f.config[0]) === String(projectId)
  })

  if (duplicate) {
    toast({ title: 'Layer already exists', description: `A Project Data layer for "${trimmedName}" already exists.` })
    return duplicate
  }

  const response = await API({
    method: 'POST',
    url: `${HOST}/org/${orgId}/layers`,
    data: {
      org_id: orgId,
      type: 4,
      name: trimmedName,
      layer: {
        aoi: null,
        data: {
          filter: {
            type: 'AndFilter',
            config: [{
              type: 'ArrayFilter',
              field_name: 'project_id',
              config: [String(projectId)]
            }]
          }
        }
      }
    }
  })

  const featureLayer = layerToFeature(response.data)
  commit('ADD_ORG_LAYER', { orgId, layer: featureLayer })
  toast({ title: 'Layer created', description: `"${trimmedName}" added to Layers.` })
  return featureLayer
}

export const addTempLayer = ({ commit }, { orgId, layer }) => {
  commit('ADD_TEMP_LAYER', { orgId, layer })
}

export const removeTempLayer = ({ commit }, { orgId, layerId }) => {
  commit('REMOVE_TEMP_LAYER', { orgId, layerId })
}

export const updateTempLayer = ({ commit }, { orgId, layer }) => {
  commit('UPDATE_TEMP_LAYER', { orgId, layer })
}

export const toggleLayerSelection = ({ commit }, { orgId, layerCode }) => {
  commit('TOGGLE_LAYER_SELECTION', { orgId, layerCode })
}

export const clearLayerSelection = ({ commit }, { orgId }) => {
  commit('CLEAR_LAYER_SELECTION', { orgId })
}

export const groupSelectedLayers = async ({ commit, state, dispatch }, { orgId }) => {
  const selected = state.selectedLayers[orgId] || {}
  const layerCodes = Object.keys(selected)
  if (layerCodes.length < 2) return

  try {
    await API({
      method: 'POST',
      url: `${HOST}/org/${orgId}/layers/group`,
      data: { layer_codes: layerCodes }
    })
    commit('CLEAR_LAYER_SELECTION', { orgId })
    await dispatch('fetchOrgLayers', { orgId })
    toast({ title: 'Group created', description: `${layerCodes.length} layers grouped.` })
  } catch (error) {
    console.error('Error grouping layers:', error)
    toast({ title: 'Failed to group layers', variant: 'destructive' })
    throw error
  }
}

export const copyLayer = ({ commit }, { orgId, layerCode, name }) => {
  commit('SET_CLIPBOARD_LAYER', { orgId, layerCode, name })
  toast({ title: 'Layer copied', description: 'Paste it onto a map from the layer menu.' })
}

// Duplicate a copied layer as a new, independent layer attached to the target map.
export const duplicateLayerToMap = async ({ dispatch }, { orgId, layerCode, mapUuid }) => {
  try {
    await API({
      method: 'POST',
      url: `${HOST}/org/${orgId}/layers/${layerCode}/duplicate`,
      data: { map_uuid: mapUuid }
    })
    await dispatch('fetchOrgLayers', { orgId })
    await dispatch('maps/fetchMaps', { orgId }, { root: true })
    toast({ title: 'Layer pasted' })
  } catch (error) {
    console.error('Error duplicating layer:', error)
    toast({ title: 'Failed to paste layer', variant: 'destructive' })
    throw error
  }
}

export const moveLayerToGroup = async ({ dispatch }, { orgId, layerCode, parentId }) => {
  try {
    await API({
      method: 'PATCH',
      url: `${HOST}/org/${orgId}/layers/${layerCode}/parent`,
      data: { parent_id: parentId }
    })
    await dispatch('fetchOrgLayers', { orgId })
  } catch (error) {
    console.error('Error moving layer to group:', error)
    toast({ title: 'Failed to move layer to group', variant: 'destructive' })
    throw error
  }
}

export const removeLayerFromGroup = async ({ commit, dispatch }, { orgId, layerCode }) => {
  try {
    await API({
      method: 'PATCH',
      url: `${HOST}/org/${orgId}/layers/${layerCode}/parent`,
      data: { parent_id: null }
    })
    await dispatch('fetchOrgLayers', { orgId })
  } catch (error) {
    console.error('Error removing layer from group:', error)
    toast({ title: 'Failed to remove layer from group', variant: 'destructive' })
    throw error
  }
}

export const removeSelectedLayersFromGroup = async ({ commit, state, dispatch }, { orgId }) => {
  const selected = state.selectedLayers[orgId] || {}
  const layerCodes = Object.keys(selected)
  if (layerCodes.length === 0) return

  try {
    await Promise.all(
      layerCodes.map(layerCode =>
        API({
          method: 'PATCH',
          url: `${HOST}/org/${orgId}/layers/${layerCode}/parent`,
          data: { parent_id: null }
        })
      )
    )
    commit('CLEAR_LAYER_SELECTION', { orgId })
    await dispatch('fetchOrgLayers', { orgId })
    toast({ title: 'Removed from group', description: `${layerCodes.length} layer${layerCodes.length > 1 ? 's' : ''} removed from group.` })
  } catch (error) {
    console.error('Error removing layers from group:', error)
    toast({ title: 'Failed to remove layers from group', variant: 'destructive' })
    throw error
  }
}
